/**
 * JotAudit Constants
 *
 * Colors, labels, and configuration values for the JotAudit dashboard
 */

import type { AuditTrigger, ComplianceStatusType, FlagPriority, TrendDirection } from './types';

/**
 * Flag priority colors (from design spec)
 */
export const FLAG_COLORS: Record<FlagPriority, string> = {
  Critical: '#EF4444', // Red
  High: '#F97316', // Orange
  Medium: '#EAB308', // Yellow
  Low: '#6B7280', // Gray
} as const;

/**
 * Flag priority background colors (lighter versions for badges)
 */
export const FLAG_BG_COLORS: Record<FlagPriority, string> = {
  Critical: '#FEE2E2', // Red-50
  High: '#FFEDD5', // Orange-50
  Medium: '#FEF9C3', // Yellow-50
  Low: '#F3F4F6', // Gray-100
} as const;

/**
 * Compliance status colors
 */
export const COMPLIANCE_COLORS: Record<ComplianceStatusType, string> = {
  yes: '#22C55E', // Green
  warning: '#EAB308', // Yellow
  no: '#EF4444', // Red
} as const;

/**
 * Compliance status background colors
 */
export const COMPLIANCE_BG_COLORS: Record<ComplianceStatusType, string> = {
  yes: '#DCFCE7', // Green-100
  warning: '#FEF9C3', // Yellow-100
  no: '#FEE2E2', // Red-100
} as const;

/**
 * Trend direction colors
 */
export const TREND_COLORS: Record<TrendDirection, string> = {
  improving: '#22C55E', // Green
  stable: '#6B7280', // Gray
  declining: '#EF4444', // Red
} as const;

/**
 * Audit trigger display labels
 */
export const TRIGGER_LABELS: Record<AuditTrigger, string> = {
  note_generated: 'Initial Audit',
  manual_reaudit: 'Re-analyzed',
  pre_sign: 'Pre-signature Check',
  post_edit: 'Post-edit Review',
} as const;

/**
 * Quality score labels (1-5 scale)
 */
export const QUALITY_LABELS: Record<number, string> = {
  1: 'Critical Issues',
  2: 'Needs Improvement',
  3: 'Acceptable',
  4: 'Good',
  5: 'Excellent',
} as const;

/**
 * Quality score colors (1-5 scale)
 */
export const QUALITY_COLORS: Record<number, string> = {
  1: '#EF4444', // Red
  2: '#F97316', // Orange
  3: '#EAB308', // Yellow
  4: '#22C55E', // Green
  5: '#16A34A', // Dark Green
} as const;

/**
 * Delta display thresholds
 */
export const DELTA_THRESHOLDS = {
  /** Delta below this is considered negative */
  negative: 0,
  /** Delta above this is considered positive */
  positive: 0,
} as const;

/**
 * Default pagination settings
 */
export const PAGINATION_DEFAULTS = {
  page: 1,
  perPage: 20,
  perPageOptions: [10, 20, 50, 100],
} as const;

/**
 * Tab configuration
 */
export const TAB_CONFIG = {
  providers: {
    label: 'Clinicians',
    value: 'providers',
  },
  rules: {
    label: 'Rules',
    value: 'rules',
  },
  notes: {
    label: 'Notes',
    value: 'notes',
  },
  claims: {
    label: 'Claims',
    value: 'claims',
  },
} as const;

/**
 * Admin tab configuration (includes organizations)
 */
export const ADMIN_TAB_CONFIG = {
  overview: {
    label: 'Overview',
    value: 'overview',
  },
  organizations: {
    label: 'Organizations',
    value: 'organizations',
  },
  providers: {
    label: 'Providers',
    value: 'providers',
  },
  notes: {
    label: 'Notes',
    value: 'notes',
  },
} as const;
